export class ApiError extends Error {
  readonly status: number;
  readonly retryAfter?: number;

  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

async function responseError(response: Response): Promise<ApiError> {
  let message = `Не удалось выполнить запрос (${response.status})`;
  let retryAfter: number | undefined;
  try {
    const payload = (await response.json()) as {
      detail?: string;
      message?: string;
      retry_after?: number;
    };
    message = payload.message ?? payload.detail ?? message;
    retryAfter = typeof payload.retry_after === "number" ? payload.retry_after : undefined;
  } catch {
    // The status code remains the useful fallback for non-JSON responses.
  }
  return new ApiError(response.status, message, retryAfter);
}

async function isCsrfFailure(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false;
  }
  try {
    const payload = (await response.clone().json()) as {
      detail?: string;
      message?: string;
    };
    return (payload.detail ?? payload.message) === "CSRF check Failed";
  } catch {
    return false;
  }
}

export async function apiGet<T>(
  path: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(path, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw await responseError(response);
  }

  return (await response.json()) as T;
}

export async function apiPost<TResponse, TBody>(
  path: string,
  body: TBody,
  signal?: AbortSignal,
): Promise<TResponse> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw await responseError(response);
  }

  return (await response.json()) as TResponse;
}

let csrfTokenPromise: Promise<string> | null = null;

async function getCsrfToken(): Promise<string> {
  if (csrfTokenPromise === null) {
    csrfTokenPromise = fetch("/api/v1/auth/csrf", {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new ApiError(response.status, "Не удалось подготовить защищённый запрос.");
        }
        const payload = (await response.json()) as { csrf_token: string };
        return payload.csrf_token;
      })
      .catch((error) => {
        csrfTokenPromise = null;
        throw error;
      });
  }
  return csrfTokenPromise;
}

export function resetCsrfToken(): void {
  csrfTokenPromise = null;
}

async function secureRequest<TResponse, TBody>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: TBody,
  signal?: AbortSignal,
  canRetryCsrf = true,
): Promise<TResponse> {
  const csrfToken = await getCsrfToken();
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (canRetryCsrf && (await isCsrfFailure(response))) {
    resetCsrfToken();
    return secureRequest(method, path, body, signal, false);
  }
  if (!response.ok) {
    throw await responseError(response);
  }

  return (await response.json()) as TResponse;
}

export function apiSecurePost<TResponse, TBody>(
  path: string,
  body: TBody,
  signal?: AbortSignal,
): Promise<TResponse> {
  return secureRequest("POST", path, body, signal);
}

export function apiPatch<TResponse, TBody>(
  path: string,
  body: TBody,
  signal?: AbortSignal,
): Promise<TResponse> {
  return secureRequest("PATCH", path, body, signal);
}

export function apiPut<TResponse, TBody>(
  path: string,
  body: TBody,
  signal?: AbortSignal,
): Promise<TResponse> {
  return secureRequest("PUT", path, body, signal);
}

export function apiDelete<TResponse>(
  path: string,
  signal?: AbortSignal,
): Promise<TResponse> {
  return secureRequest("DELETE", path, undefined, signal);
}
